'use client'

import { useRef, useState } from 'react'
import { FileUp, ShieldCheck, X } from 'lucide-react'

export type MediaUploadAsset = {
  id: string
  originalName: string
  mimeType: string
  sizeBytes: number
  visibility: 'public' | 'protected' | 'private' | 'admin_only' | 'private_sensitive'
  purpose: string
  url?: string | null
}

interface MediaUploadProps {
  label?: string
  ownerType: 'masjid' | 'donation_project' | 'expense' | 'claim' | 'report' | 'announcement' | 'waqf_document'
  ownerId: string
  masjidSlug?: string
  masjidId?: string
  visibility?: 'public' | 'protected' | 'private' | 'admin_only' | 'private_sensitive'
  purpose?: 'gallery' | 'proof' | 'document' | 'receipt' | 'announcement_image'
  accept?: string
  mode?: 'image' | 'document' | 'any'
  value?: string
  helper?: string
  onUploaded: (asset: MediaUploadAsset) => void
  onClear?: () => void
}

function fileSize(size: number) {
  if (size > 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(size / 1024))} KB`
}

export default function MediaUpload({
  label = 'Upload proof',
  ownerType,
  ownerId,
  masjidSlug,
  masjidId,
  visibility = 'private',
  purpose = 'proof',
  accept,
  mode = 'any',
  value,
  helper,
  onUploaded,
  onClear,
}: MediaUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [asset, setAsset] = useState<MediaUploadAsset | null>(null)

  const acceptString = accept || (
    mode === 'image' ? 'image/jpeg,image/png,image/webp,image/gif,image/avif' :
    mode === 'document' ? 'application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.txt' :
    'image/jpeg,image/png,image/webp,image/gif,image/avif,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.txt'
  )

  async function handleFile(file: File) {
    setUploading(true)
    setError('')
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('ownerType', ownerType)
      form.append('ownerId', ownerId)
      if (masjidSlug) form.append('masjidSlug', masjidSlug)
      if (masjidId) form.append('masjidId', masjidId)
      form.append('visibility', visibility)
      form.append('purpose', purpose)

      const res = await fetch('/api/media/upload', { method: 'POST', body: form })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data.error || 'Upload failed.')
        return
      }
      setAsset(data.asset)
      onUploaded(data.asset)
    } catch {
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const shownName = asset?.originalName || value

  return (
    <div className="mediaUpload">
      <div className="mediaUploadLabel">
        <span><ShieldCheck size={14} /> {label}</span>
        {shownName && onClear && (
          <button type="button" onClick={() => { setAsset(null); onClear() }} aria-label="Remove uploaded file"><X size={13} /></button>
        )}
      </div>
      <button
        type="button"
        className="drop"
        disabled={uploading || !ownerId}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault()
          const file = event.dataTransfer.files?.[0]
          if (file) handleFile(file)
        }}
      >
        <FileUp size={18} />
        <strong>{uploading ? 'Uploading...' : shownName ? String(shownName).slice(0, 42) : 'Click or drag to upload'}</strong>
        <small>{asset ? `${asset.visibility} - ${fileSize(asset.sizeBytes)}` : helper || 'Images, PDF, Word, Excel, or text. Max 15MB.'}</small>
      </button>
      {error && <p className="mediaError">{error}</p>}
      <input ref={inputRef} type="file" accept={acceptString} hidden onChange={(event) => {
        const file = event.target.files?.[0]
        if (file) handleFile(file)
      }} />
      <style jsx>{`
        .mediaUpload { display: grid; gap: 8px; min-width: 0; }
        .mediaUploadLabel { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .mediaUploadLabel span { display: inline-flex; align-items: center; gap: 7px; color: #64748b; font-size: 11px; font-weight: 900; letter-spacing: .04em; text-transform: uppercase; }
        .mediaUploadLabel button { display: inline-grid; place-items: center; border: 1px solid #fecaca; border-radius: 999px; color: #b42318; background: #fff7f7; width: 28px; height: 28px; cursor: pointer; }
        .drop { display: grid; place-items: center; gap: 6px; min-height: 112px; width: 100%; border: 1px dashed #b9cbc4; border-radius: 14px; color: #12312d; background: linear-gradient(145deg, #fff, #fbfdfa); padding: 16px; cursor: pointer; text-align: center; }
        .drop:disabled { cursor: not-allowed; opacity: .62; }
        .drop svg { color: #0e7c61; }
        .drop strong { max-width: 100%; overflow: hidden; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
        .drop small { color: #64748b; font-size: 11px; font-weight: 750; line-height: 1.45; }
        .mediaError { margin: 0; color: #b42318; font-size: 12px; font-weight: 800; }
      `}</style>
    </div>
  )
}
